import type { AsyncComponentLoader } from "vue";

/**
 * URL d'API publique avec `preview=true` et/ou `src=demo` selon le mode
 * (`src=demo` : page affichée dans la démo de la home, cf. useDemoAccueil →
 * ni visite comptée, ni statistique pour l'association).
 */
export function urlApiPublique(
  base: string,
  opts: { preview?: boolean; demo?: boolean },
): string {
  const params = new URLSearchParams();
  if (opts.preview) params.set("preview", "true");
  if (opts.demo) params.set("src", "demo");
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}


/** Les démos de la home (« Tout ce dont vous avez besoin » et la vitrine des outils). */
export type TypeDemoAccueil = "don" | "cagnotte" | "boutique" | "adhesion" | "billetterie";

/**
 * Les exemples RÉELS montrés dans les démos de la home, choisis par Karim
 * (les plus parlants). Une seule liste pour les deux sections qui les
 * affichent (DonateDemoSection et VitrineOutils) : changer un exemple ici le
 * change partout.
 */
export const EXEMPLES_DEMO_ACCUEIL: Record<
  TypeDemoAccueil,
  { slug: string; reglages?: Record<string, string> }
> = {
  don: {
    // Lien de paiement de Donoor elle-même.
    slug: "donoor",
    // Réglages du formulaire (mêmes clés que les paramètres d'embed.js) :
    // en 2 étapes, sans badge.
    reglages: {
      w: "full",
      steps: "2",
      hideBadge: "1",
      // Couleur TÉMOIN : dans « Tout ce dont vous avez besoin », elle est
      // remplacée par celle de la photo tirée au sort (DonateDemoVariantes.css,
      // qui cherche exactement cette valeur). Ne pas la changer sans y
      // reporter la nouvelle valeur.
      primary: "#0b5f8f",
      // Boutons en pilules : montants rapides, « Suivant », « Faire un don »,
      // « Soutenir Donoor » (Karim 2026-10-13).
      buttons: "pill",
      amounts: "10,20,50,100,200,500",
    },
  },
  cagnotte: { slug: "sauvons-la-11e-edition-du-festival-les-aiguilleurs" },
  boutique: { slug: "petits-pigeons-de-coeur" },
  adhesion: { slug: "adhesion-contrat-elite-2027" },
  // ⚠️ Événement daté (24/10/2026) : à remplacer une fois passé.
  billetterie: { slug: "dark-romance-party" },
};

/**
 * Le code de chaque démo : la VRAIE page publique (ou le vrai formulaire de
 * don), chargée seulement quand la démo est affichée ou sur le point de l'être.
 */
export const PAGES_DEMO_ACCUEIL: Record<TypeDemoAccueil, AsyncComponentLoader> = {
  don: () => import("~/components/donate/DonateEmbedApp.vue"),
  cagnotte: () => import("~/pages/c/[slug]/index.vue"),
  boutique: () => import("~/pages/boutique/[slug]/index.vue"),
  adhesion: () => import("~/pages/a/[slug]/index.vue"),
  billetterie: () => import("~/pages/b/[slug]/index.vue"),
};

/**
 * Données d'une démo chargées par du code de PAIEMENT, auquel on ne touche
 * pas (`useDonationData`, clé `donation-<slug>`) : leur clé ne peut pas porter
 * la marque de la démo (cf. useDonneeDemoAccueil). DemoAccueilPage remet
 * elle-même leur rendu du serveur à portée de la page, le temps qu'elle
 * démarre — sans ça, le formulaire repartait de valeurs par défaut.
 */
export const CLES_PAIEMENT_DEMO_ACCUEIL: Partial<Record<TypeDemoAccueil, (slug: string) => string[]>> = {
  don: (slug) => [`donation-${slug}`],
};

/**
 * Zone qui défile autour d'une page affichée dans la démo de la home (null
 * hors démo). Dans la démo, c'est elle — pas la fenêtre — qui tient lieu
 * d'écran pour la page : défilement, hauteur visible, sections à l'écran.
 */
export function zoneDemoAccueil(el: Element | null | undefined): HTMLElement | null {
  return el?.closest<HTMLElement>("[data-demo-defilement]") ?? null;
}

/**
 * Amène `el` en haut de la zone de démo (moins `decalage` et sa marge de
 * défilement CSS), SANS bouger la home. Renvoie faux hors démo : l'appelant
 * fait alors défiler la fenêtre comme d'habitude.
 */
export function defilerDansDemoAccueil(el: HTMLElement, decalage = 0): boolean {
  const zone = zoneDemoAccueil(el);
  if (!zone) return false;
  // Sur ordinateur, la démo est réduite (transform) : les mesures à l'écran
  // sont à ramener à l'échelle de la page.
  const cadreZone = zone.getBoundingClientRect();
  const echelle = zone.offsetHeight ? cadreZone.height / zone.offsetHeight : 1;
  const marge = Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
  const haut = (el.getBoundingClientRect().top - cadreZone.top) / echelle + zone.scrollTop;
  zone.scrollTo({ top: Math.max(0, haut - marge - decalage), behavior: "smooth" });
  return true;
}
